import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MenuOverlay from '../components/MenuOverlay';
import CartDrawer from '../components/shop/CartDrawer';
import ProductGrid from '../components/shop/ProductGrid';
import useCart from '../hooks/useCart';
import { PRODUCTS } from '../data/products';

const ShopProduct = () => {
  const { productId } = useParams();
  const product = PRODUCTS.find((p) => p.id === productId);
  const navigate = useNavigate();
  const cart = useCart();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [added, setAdded] = useState(false);

  // Redirect if not found
  useEffect(() => {
    if (!product) navigate('/shop', { replace: true });
  }, [product, navigate]);

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Enable scrolling
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    body.style.position = 'static';
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      root.style.position = 'static';
    }
    return () => {
      html.style.overflow = 'hidden';
      html.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
      body.style.position = 'fixed';
      if (root) {
        root.style.overflow = 'hidden';
        root.style.height = '100%';
        root.style.position = 'fixed';
      }
    };
  }, []);

  // Scroll to top on product change
  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImage(0);
    setAdded(false);
  }, [productId]);

  if (!product) return null;

  const related = PRODUCTS.filter(
    (p) => p.categoryId === product.categoryId && p.id !== product.id,
  );

  const handleAdd = () => {
    cart.addToCart(product.id);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div style={{ background: '#000', minHeight: '100vh' }}>
      {/* Navigation */}
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          padding: isMobile ? '16px 12px' : '32px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(rgba(0,0,0,0.6), transparent)',
        }}
      >
        <div style={{ width: 80 }}>
          <button
            onClick={() => navigate('/shop')}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 300,
              fontSize: 13,
              letterSpacing: '0.1em',
              padding: 0,
            }}
          >
            &larr; SHOP
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <h1
            onClick={() => navigate('/')}
            className="tracking-widest uppercase cursor-pointer"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 300,
              fontSize: isMobile ? '14px' : '22px',
              color: '#ffffff',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              margin: 0,
            }}
          >
            Joshua Gavin
          </h1>
        </div>
        <div style={{ width: 80, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 20 }}>
          <button
            onClick={() => setIsCartOpen(true)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, position: 'relative' }}
            aria-label="Open cart"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
            {cart.cartCount > 0 && (
              <span style={{ position: 'absolute', top: -6, right: -8, background: '#36454F', color: '#fff', fontSize: 10, fontWeight: 600, width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif' }}>
                {cart.cartCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setIsMenuOpen(true)}
            className="flex flex-col items-end gap-2 bg-transparent border-none cursor-pointer p-0 group"
            aria-label="Open menu"
          >
            <span className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
            <span className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
            {isMobile && (
              <span className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
            )}
          </button>
        </div>
      </nav>

      {/* Product Content */}
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px',
          display: isMobile ? 'block' : 'flex',
          gap: 64,
        }}
      >
        {/* Image Gallery */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: '100%',
              aspectRatio: '4/3',
              overflow: 'hidden',
              borderRadius: 2,
              background: '#111',
              marginBottom: 12,
            }}
          >
            <img
              src={product.images[activeImage]}
              alt={product.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </div>
          {product.images.length > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              {product.images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    border: 'none',
                    background: i === activeImage ? '#ffffff' : 'rgba(255,255,255,0.3)',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'background 0.2s',
                  }}
                  aria-label={`View image ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            paddingTop: isMobile ? 32 : 0,
          }}
        >
          <h1
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500,
              fontSize: 'clamp(20px, 3vw, 28px)',
              color: '#ffffff',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            {product.title}
          </h1>
          <p
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 300,
              fontSize: 20,
              color: 'rgba(255,255,255,0.6)',
              margin: '12px 0 28px',
            }}
          >
            ${product.price.toFixed(2)}
          </p>
          <p
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 300,
              fontSize: 15,
              color: 'rgba(255,255,255,0.7)',
              lineHeight: 1.8,
              margin: '0 0 28px',
            }}
          >
            {product.description}
          </p>

          {/* Details list */}
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 36px',
            }}
          >
            {product.details.map((detail, i) => (
              <li
                key={i}
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 300,
                  fontSize: 14,
                  color: 'rgba(255,255,255,0.55)',
                  padding: '6px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>
                {detail}
              </li>
            ))}
          </ul>

          {/* Add to Cart */}
          <button
            onClick={handleAdd}
            style={{
              width: '100%',
              maxWidth: 360,
              padding: '15px 0',
              background: added ? '#22c55e' : '#36454F',
              color: '#ffffff',
              border: 'none',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500,
              fontSize: 14,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              borderRadius: 2,
              transition: 'background 0.3s ease, color 0.3s ease',
            }}
            onMouseEnter={(e) => {
              if (!added) {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.color = '#000000';
              }
            }}
            onMouseLeave={(e) => {
              if (!added) {
                e.currentTarget.style.background = '#36454F';
                e.currentTarget.style.color = '#ffffff';
              }
            }}
          >
            {added ? 'Added to Cart' : 'Add to Cart'}
          </button>
        </div>
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <>
          <div style={{ maxWidth: 120, margin: '0 auto', borderTop: '1px solid rgba(255,255,255,0.1)' }} />
          <ProductGrid products={related} title="Related" />
        </>
      )}

      {/* Footer */}
      <footer
        style={{
          textAlign: 'center',
          padding: '48px 24px 64px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 300,
            fontSize: 12,
            color: 'rgba(255,255,255,0.25)',
            letterSpacing: '0.05em',
          }}
        >
          &copy; {new Date().getFullYear()} Joshua Gavin. All rights reserved.
        </p>
      </footer>

      <MenuOverlay isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} cart={cart} />
    </div>
  );
};

export default ShopProduct;
